/**
 * The screen the board becomes once the lesson is over (SRS I1 step 5).
 *
 * It replaces the board rather than sitting on top of it. What is behind it is a copy
 * of a lesson that has already been filed: anything drawn on it now would be kept by
 * this browser and by nothing else, and a board that quietly stops saving is worse
 * than one that is plainly finished.
 *
 * The teacher and the student are told the same fact and given different doors, so
 * neither is left on a screen with nowhere to go.
 */

import { useEffect, useState } from 'react';

import { DRIVE_CONNECT_PATH, fetchDriveConnected } from '../../api.js';
import { Logo } from '../../assets/Logo.js';
import { navigate } from '../../router.js';

export interface SessionEndedProps {
  readonly role: 'teacher' | 'student';
}

/**
 * SRS I4 — the one moment a teacher is asked for Google Drive.
 *
 * Asked here and nowhere else, because this is the first point at which there is
 * something to file. The lesson is already saved either way, so this is an offer
 * rather than a demand, and the wording says so.
 */
function DriveOffer() {
  const [connected, setConnected] = useState<boolean | null>(null);

  useEffect(() => {
    void fetchDriveConnected().then(setConnected);
  }, []);

  if (connected !== false) return null;

  return (
    <div className="flex max-w-[420px] flex-col items-center gap-3 rounded-lg border border-border bg-primary-subtle p-6">
      <p className="text-base text-text-primary">
        Keep a copy of your lessons in your own Google Drive?
      </p>
      <a
        href={DRIVE_CONNECT_PATH}
        className="flex h-touch items-center rounded-md bg-surface px-6 text-base font-medium text-text-primary transition duration-fast ease-out hover:bg-surface-hover"
      >
        Connect Google Drive
      </a>
    </div>
  );
}

export function SessionEnded({ role }: SessionEndedProps) {
  const teaching = role === 'teacher';
  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-6 bg-surface-sunken p-6 text-center">
      <Logo size={40} />
      <p className="text-lg text-text-primary">
        {teaching ? 'This lesson has ended.' : 'Your teacher has ended the lesson.'}
      </p>
      <p className="text-base text-text-secondary">Everything on the board has been saved.</p>
      {teaching ? <DriveOffer /> : null}
      <button
        type="button"
        onClick={() => {
          navigate(teaching ? '/home' : '/join');
        }}
        className="h-touch rounded-md bg-primary px-6 text-base font-medium text-text-inverse transition duration-fast ease-out hover:bg-primary-hover"
      >
        {teaching ? 'Back to my lessons' : 'Join another class'}
      </button>
    </main>
  );
}
